import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { screen } from '@/components/common/breakpoints';
import { ArrowButtonLinkLeft } from '@/components/common/Button';
import {
  determineDestination,
  VisuallyHidden,
} from '@/components/common/utils';
import { PostTableOfContents } from '@/components/page/blog/PostTableOfContents';
import { PortableText } from '@/lib/sanity';
import {
  bodyMediumSemiBold,
  bodySmall,
  subheadline,
} from '@/styles/typography';

import type { SanityBlogPostTocDataType } from '@/components/page/blog/PostTableOfContents';
import type { MutableRefObject } from 'react';

const getComponents = () => {
  const components = {
    marks: {
      strong: ({ children }) => <b>{children}</b>,
      internalLink: ({ value, children }) => {
        if (!value.item) return children;

        let prefix = '';

        switch (value._type) {
          case 'blogPost':
            prefix = 'blog/';
            break;
          case 'programme':
            prefix = 'programme';
            break;
          case 'page':
          default:
            prefix = '';
            break;
        }
        const href = `/${prefix}${value.item.slug.current}`;

        return <Link href={href}>{children}</Link>;
      },
      link: ({ value, children }) => {
        const { href } = value;

        if (!href) {
          return <span>{children}</span>;
        }

        const destination = determineDestination(href);
        return (
          <a
            href={href}
            target={destination.target}
            rel={
              destination.isExternalLink ? 'external noopener noreferrer' : null
            }
          >
            {children}
            {destination.isExternalLink && (
              <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
            )}
          </a>
        );
      },
    },
  };
  return components;
};
export interface PostExcerptProps {
  excerpt: any;
  postContentRef: MutableRefObject<HTMLDivElement>;
  standoutKeys: Array<string>;
  tocData: SanityBlogPostTocDataType;
}
type anchorToType = 'NONE' | 'TOP' | 'BOTTOM';

const PostExcerpt = ({
  excerpt,
  postContentRef,
  standoutKeys,
  tocData,
}: PostExcerptProps) => {
  const { locale } = useRouter();

  const [anchorTo, setAnchorTo] = useState<anchorToType>('NONE');
  const stickyRef = useRef(null);
  const arrowRef = useRef(null);
  const tocRef = useRef(null);

  let contentBottom = 0;

  if (postContentRef?.current && standoutKeys.length) {
    const standoutElements: Element[] = Array.from(
      postContentRef.current.children,
    ).filter((child) => standoutKeys.includes(child.id));
    contentBottom = standoutElements?.reduce((acc, node) => {
      return acc + node.scrollHeight;
    }, contentBottom);

    contentBottom = contentBottom ? contentBottom + 132 : 0;
  }

  const postExcerptHeight = stickyRef.current
    ? window.scrollY + stickyRef.current.getBoundingClientRect().top
    : 0;

  useEffect(() => {
    const handleScroll = () => {
      const postContentPos = postContentRef.current
        ? postContentRef.current.getBoundingClientRect().height +
          postContentRef.current.getBoundingClientRect().top
        : 0;

      const arrowHeight = arrowRef?.current
        ? arrowRef.current.getBoundingClientRect().height
        : 0;

      const tocHeight = tocRef?.current
        ? tocRef.current.getBoundingClientRect().height
        : 0;

      //     Table of Contents height
      //     Back to posts arrow height
      //     --spacing-800
      //  +  --spacing-500 x 2
      const arrowContainerHeight =
        tocHeight + arrowHeight + contentBottom + 64 + 2 * 40;

      if (
        stickyRef.current &&
        stickyRef.current.getBoundingClientRect().top < 2
      ) {
        if (postContentPos < arrowContainerHeight) {
          setAnchorTo('BOTTOM');
        } else {
          setAnchorTo('TOP');
        }
      } else {
        setAnchorTo('NONE');
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [contentBottom, postExcerptHeight, postContentRef]);

  return (
    <Container ref={stickyRef} anchorTo={anchorTo} bottom={contentBottom}>
      <LeftContainer>
        <ArrowContainer>
          <div ref={arrowRef}>
            <ArrowButtonLinkLeft
              backgroundOfParent="white"
              href={`/${locale}/blog/`}
            >
              See all posts
            </ArrowButtonLinkLeft>
          </div>
          {!!tocData.count && (
            <PostTableOfContents ref={tocRef} tocData={tocData} />
          )}
        </ArrowContainer>
      </LeftContainer>
      <ContentContainer>
        <PortableText value={excerpt} components={getComponents()} />
      </ContentContainer>
    </Container>
  );
};

const Container = styled.div<{ anchorTo: anchorToType; bottom: number }>`
  --arrow-container-position: relative;
  --arrow-container-top: 0px;
  --arrow-container-bottom: unset;
  --content-container-padding-left: 66px;
  --content-container-padding-right: 142px;
  --content-container-width: 1184px;
  --toc-content-container-position: absolute;
  --toc-content-container-width: 230px;

  ${(props) =>
    props.anchorTo === 'TOP' &&
    '--arrow-container-position: fixed;' +
      '--arrow-container-top: 74px;' +
      '--content-container-padding-left: 398px;' +
      '--content-container-padding-right: 136px;' +
      '--content-container-width: 1195px;'}

  ${(props) =>
    props.anchorTo === 'BOTTOM' &&
    '--arrow-container-position: absolute;' +
      '--arrow-container-top: unset;' +
      `--arrow-container-bottom: ${props.bottom}px;` +
      '--toc-content-container-position: static;'}

  ${screen.md} {
    display: flex;
    flex-direction: row;
    padding: var(--spacing-800) 0 var(--spacing-small);
  }
`;
const ContentContainer = styled.div`
  padding-top: var(--spacing-small);
  padding-bottom: var(--spacing-small);

  width: 100%;

  a {
    color: var(--text-action);
  }
  p {
    ${bodyMediumSemiBold}
    font-size: var(--font-size-medium);
  }
  h2 {
    ${subheadline}
  }
  em,
  i {
    font-style: italic;
  }

  ${screen.md} {
    width: calc(100% - var(--toc-content-container-width));
    padding-left: var(--spacing-x-large);
  }
`;

const LeftContainer = styled.div`
  ${screen.md} {
    width: var(--toc-content-container-width);
  }
`;

const ArrowContainer = styled.div`
  padding: var(--spacing-small) 0;
  z-index: 1;

  span {
    ${bodySmall}
  }

  ${screen.md} {
    position: var(--arrow-container-position);
    top: var(--arrow-container-top);
    bottom: var(--arrow-container-bottom);
    margin: 0;
    padding: var(--spacing-medium) var(--spacing-700) var(--spacing-small) 0;
  }
`;
export { PostExcerpt };
